import { ArrayUtils } from "neo-common";
import { Configuration } from "../Components/Configuration";
import { Bet } from "../GameComponents/Bet";
import { Communication } from "neo-games";
import { AppContext } from "../../Common/AppContext";
import { DataManager } from "./DataManager";
import { Scaling } from "../Utils/Scaling";
import { ModuleManager } from "./ModuleManager";
import { HtmlController } from "../HTMLComponents/HtmlController";
import { GameApp } from "../GameApp";

export class SchemasManager {
    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    private static DefaultSchemas: Array<Array<Bet>> = [];

    private static PlayerSchemas: Array<Array<Bet>> = [];

    private static DefaultCount: number = 8;

    private static PlayerCount: number = 8;

    static CurrentPlayerSettings: PlayerSettings;

    //******************************************************************************************************************
    //  INIT
    //******************************************************************************************************************

    public static initialize(): void {
        SchemasManager.parsePlayerData();
        SchemasManager.parseDefaultData();
    }

    private static parsePlayerData(): void {
        let playerData = AppContext.playerConfiguration.getConfig("schemas") as Array<
            Array<Communication.Game.RouletteSpinBet>
        >;
        let playerSettings = AppContext.playerConfiguration.getConfig("settings") as PlayerSettings;
        if (playerSettings !== null) {
            SchemasManager.CurrentPlayerSettings = {
                quickspin: playerSettings.quickspin,
                theme: playerSettings.theme,
                ambientSound: playerSettings.ambientSound,
                effectsSound: playerSettings.effectsSound,
                commentarySound: playerSettings.commentarySound,
                commentaryGender: playerSettings.commentaryGender,
                commentaryLanguage:
                    AppContext.gameConfiguration.NarratorLanguages.indexOf(playerSettings.commentaryLanguage) !== -1
                        ? playerSettings.commentaryLanguage
                        : "EN",
                leftHandMode: playerSettings.leftHandMode,
                masterVolume: playerSettings.masterVolume,
            };
        } else {
            let language: string;
            //TODO add proper 4 letter language support to narrator
            if (GameApp.language.indexOf("-") == -1 || GameApp.language.indexOf("BR") != -1) {
                language = GameApp.language.toUpperCase();
            } else {
                language = GameApp.language.substring(0, GameApp.language.indexOf("-")).toUpperCase();
            }
            SchemasManager.CurrentPlayerSettings = {
                quickspin: false,
                theme: "gold",
                ambientSound: true,
                effectsSound: true,
                commentarySound: true,
                commentaryGender: false,
                commentaryLanguage:
                    AppContext.gameConfiguration.NarratorLanguages.indexOf(language) !== -1 ? language : "EN",
                leftHandMode: false,
                masterVolume: 3,
            };
        }

        for (let i = 0; i < SchemasManager.PlayerCount; i++) {
            const schema: Array<Bet> = [];

            if (playerData != null) {
                const schemaData = playerData[i];

                if (schemaData != null) {
                    for (let j = 0; j < schemaData.length; j++) {
                        let betData = schemaData[j];

                        if (betData != null) {
                            schema.push(new Bet(betData.type, betData.selection, betData.value));
                        }
                    }
                }
            }

            SchemasManager.PlayerSchemas.push(schema);
        }
    }

    private static parseDefaultData(): void {
        let config: Element = Configuration.Configurations["DefaultSchemas"];
        if (config != null) {
            if (config.hasAttribute("Count")) {
                SchemasManager.DefaultCount = parseInt(config.getAttribute("Count"));
            }
        }

        for (let i = 0; i < SchemasManager.DefaultCount; i++) {
            const schema: Array<Bet> = [];

            if (config != null) {
                if (config.hasAttribute("Schema" + i)) {
                    let bets = ArrayUtils.stringArray(config.getAttribute("Schema" + i), "|");
                    for (let j = 0; j < bets.length; j++) {
                        let betArray = ArrayUtils.stringArray(bets[j]);

                        if (betArray.length == 3) {
                            //type, selection, value
                            schema.push(new Bet(betArray[0], parseInt(betArray[1]), parseInt(betArray[2])));
                        }
                    }
                }
            }

            SchemasManager.DefaultSchemas.push(schema);
        }
    }

    //******************************************************************************************************************
    //  HANDLERS
    //******************************************************************************************************************

    public static addSchema(index: number, schema: Array<Bet>): void {
        if (SchemasManager.PlayerSchemas.length > index) {
            SchemasManager.PlayerSchemas[index] = schema;

            SchemasManager.updateSchemas();
        }
    }

    public static removeSchema(index: number): void {
        if (SchemasManager.PlayerSchemas.length > index) {
            SchemasManager.PlayerSchemas[index] = [];

            SchemasManager.updateSchemas();
        }
    }

    public static getAllPlayerSchemas(): Array<Array<Bet>> {
        return SchemasManager.PlayerSchemas;
    }

    public static getPlayerSchemaByIndex(index: number): Array<Bet> {
        if (SchemasManager.PlayerSchemas != null && SchemasManager.PlayerSchemas[index] != null) {
            return SchemasManager.PlayerSchemas[index];
        }

        return [];
    }

    public static getDefaultSchemaByIndex(index: number): Array<Bet> {
        if (SchemasManager.DefaultSchemas != null && SchemasManager.DefaultSchemas[index] != null) {
            return SchemasManager.DefaultSchemas[index];
        }

        return [];
    }

    public static getDefaultSchemasCount(): number {
        return SchemasManager.DefaultCount;
    }

    public static getPlayerSchemasCount(): number {
        return SchemasManager.PlayerCount;
    }

    private static updateSchemas(): void {
        AppContext.playerConfiguration.update({ schemas: SchemasManager.getSchemaData(SchemasManager.PlayerSchemas) });
    }

    public static getSchemaData(schemas: Array<Array<Bet>>): Array<Array<Communication.Game.RouletteSpinBet>> {
        let result: Array<Array<Communication.Game.RouletteSpinBet>> = [];

        for (let i = 0; i < schemas.length; i++) {
            let bets: Array<Communication.Game.RouletteSpinBet> = [];
            for (let j = 0; j < schemas[i].length; j++) {
                let bet = new Communication.Game.RouletteSpinBet(
                    schemas[i][j].type,
                    schemas[i][j].selection,
                    schemas[i][j].value,
                );
                bets.push(bet);
            }
            result.push(bets);
        }

        return result;
    }

    public static updatePlayerSettings(settings: any): void {
        AppContext.playerConfiguration.update({ settings: settings });
    }

    public static getSettingsData(settings: PlayerSettings): PlayerSettings {
        return settings;
    }
}

/**
 * @class PlayerSettings - information about player settings.
 */
export interface PlayerSettings {
    /**
     * @property theme - Color of the table.
     */
    theme: string;

    /**
     * @property ambientSound - Background music on/off.
     */
    ambientSound: boolean;

    /**
     * @property effectsSound - Game effect sounds on/off.
     */
    effectsSound: boolean;

    /**
     * @property commentarySound - Commentary voice on/off.
     */
    commentarySound: boolean;

    /**
     * @property commentaryGender - Commentary voice gender male/female.
     */
    commentaryGender: boolean;

    /**
     * @property commentaryGender - Commentary voice gender male/female.
     */
    commentaryLanguage: string;

    /**
     * @property leftHandMode - Commentary voice gender male/female.
     */
    leftHandMode: boolean;

    /**
     * @property quickspin - Toggle spin animation skip on/off.
     */
    quickspin: boolean;

    /**
     * @property quickspin - Toggle spin animation skip on/off.
     */
    masterVolume: number;
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/Managers/SchemasManager.ts
 **/